import { Fragment, type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { oneSectionTopWidgetHomeV2Queue$key } from "scmp-app/queries/__generated__/oneSectionTopWidgetHomeV2Queue.graphql";

import {
  BottomItem,
  BottomRow,
  Container,
  FullDivider,
  ItemDivider,
  MidItemWrapper,
  MidRow,
  StyledLead,
  StyledMidItem,
  StyledSmallItem,
} from "./styles";

type Props = ContentItemRenderVariantProps & {
  reference: oneSectionTopWidgetHomeV2Queue$key;
};

export const HomeV2SectionTopWidgetOne: FunctionComponent<Props> = ({
  className,
  onClick,
  reference,
}) => {
  const data = useFragment(
    graphql`
      fragment oneSectionTopWidgetHomeV2Queue on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            entityId
            ...homeSecondaryV2ContentItemContent
            ...leadSectionTopHomeV2Content
          }
        }
      }
    `,
    reference,
  );

  const items = (data ?? []).map(({ node }) => node).filter(node => notEmpty(node.entityId));

  if (items.length === 0) return null;

  const [leading, ...rest] = items;
  const midItems = rest.slice(0, 2);
  const smallItems = rest.slice(2, 6);

  return (
    <Container className={className}>
      <StyledLead onClick={onClick} reference={leading} useWideCoverImage />

      {midItems.length > 0 && (
        <>
          <FullDivider />
          <MidRow>
            {midItems.map((node, index) => (
              <Fragment key={node.entityId}>
                {index > 0 && <ItemDivider />}
                <MidItemWrapper>
                  <StyledMidItem
                    isReadingTimeEnabled
                    onClick={onClick}
                    reference={node}
                    withComment
                    withImage
                    withLiveTag
                  />
                </MidItemWrapper>
              </Fragment>
            ))}
          </MidRow>
        </>
      )}

      {smallItems.length > 0 && (
        <>
          <FullDivider />
          <BottomRow>
            {smallItems.map((node, index) => (
              <Fragment key={node.entityId}>
                {index > 0 && <ItemDivider />}
                <BottomItem>
                  <StyledSmallItem
                    isReadingTimeEnabled
                    onClick={onClick}
                    reference={node}
                    withComment
                  />
                </BottomItem>
              </Fragment>
            ))}
          </BottomRow>
        </>
      )}
    </Container>
  );
};

HomeV2SectionTopWidgetOne.displayName = "HomeV2SectionTopWidgetOne";
